import { useCallback, useEffect, useRef, useState } from 'react'
import { NestAPI, useGetBusinessID } from '@marketing-milk/frontend'
import { FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { getApiErrorMessage } from 'app/util'
import { toast } from 'react-toastify'

/**
 * Module-level preview cache shared across hook instances. The preview HTML for
 * a `(business, creative, format)` is a stable, expensive live Facebook fetch,
 * so caching it lets a hover-warmed preview render instantly when its modal
 * later mounts (see sprint-packet creative cards). `inflight` dedupes concurrent
 * requests (e.g. a hover prefetch racing the modal's own mount fetch).
 */
const cacheKey = (businessID: string | number, creativeID: number, format?: FBAdPreviewFormat) =>
  `${businessID}:${creativeID}:${format ?? 'default'}`

const previewCache = new Map<string, string>()
const inflight = new Map<string, Promise<string>>()
// Bumped per key on every invalidation. A request captures the generation at
// dispatch and only writes its result back if the key hasn't been invalidated
// since — otherwise a request started before an edit could repopulate the cache
// with pre-edit HTML after we cleared it.
const generation = new Map<string, number>()

const bumpGeneration = (key: string) => generation.set(key, (generation.get(key) ?? 0) + 1)

// A live FB preview generation can hang (slow Graph API, expired token retried
// server-side). Cap the wait so the caller falls back to the local preview
// instead of spinning forever; the underlying request is left running so it can
// still populate the cache if it eventually completes.
const PREVIEW_TIMEOUT_MS = 8000

export class PreviewTimeoutError extends Error {
  constructor() {
    super('Timed out generating a Facebook preview')
    this.name = 'PreviewTimeoutError'
  }
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new PreviewTimeoutError()), ms)
    promise.then(
      value => {
        clearTimeout(timer)
        resolve(value)
      },
      error => {
        clearTimeout(timer)
        reject(error)
      }
    )
  })
}

/**
 * Drop cached (and in-flight) preview HTML for a creative so the next fetch is
 * live. A content edit happens in a separate Creative Wizard tab (its own module
 * cache), so the packet tab must bust its own cache before re-displaying or
 * approving — otherwise an AE could approve stale ad HTML. With no `format`,
 * clears every cached format for the creative.
 */
export function invalidateCreativePreview(
  businessID: string | number,
  creativeID: number,
  format?: FBAdPreviewFormat
): void {
  if (format !== undefined) {
    const key = cacheKey(businessID, creativeID, format)
    previewCache.delete(key)
    inflight.delete(key)
    bumpGeneration(key)
    return
  }
  const prefix = `${businessID}:${creativeID}:`
  Array.from(previewCache.keys()).forEach(key => {
    if (key.startsWith(prefix)) previewCache.delete(key)
  })
  Array.from(inflight.keys()).forEach(key => {
    if (key.startsWith(prefix)) inflight.delete(key)
  })
  // Bump generations for every known key under this creative (in-flight requests
  // register their key, below) so any pending request is barred from writing back.
  Array.from(generation.keys()).forEach(key => {
    if (key.startsWith(prefix)) bumpGeneration(key)
  })
}

/** Synchronously read a previously fetched preview, if any. */
export function peekCreativePreview(
  businessID: string | number,
  creativeID: number,
  format?: FBAdPreviewFormat
): string | undefined {
  return previewCache.get(cacheKey(businessID, creativeID, format))
}

/** Fetch (or reuse) the preview HTML, populating the shared cache. Throws on failure. */
export async function fetchCreativePreview(
  businessID: string | number,
  creativeID: number,
  format?: FBAdPreviewFormat
): Promise<string> {
  const key = cacheKey(businessID, creativeID, format)
  const cached = previewCache.get(key)
  if (cached !== undefined) return cached
  const pending = inflight.get(key)
  if (pending) return pending

  // Register the key and snapshot its generation; an invalidation between now and
  // resolution bumps it, telling us not to cache a now-stale response.
  if (!generation.has(key)) generation.set(key, 0)
  const dispatchedGen = generation.get(key) ?? 0

  const request = NestAPI.get(`businesses/${businessID}/ad-creatives/${creativeID}/preview`, {
    params: format ? { format } : undefined,
  })
    .then(response => {
      if (generation.get(key) === dispatchedGen) previewCache.set(key, response.data)
      if (inflight.get(key) === request) inflight.delete(key)
      return response.data as string
    })
    .catch(e => {
      if (inflight.get(key) === request) inflight.delete(key)
      throw e
    })
  inflight.set(key, request)
  // Race the awaited result against a timeout so a hung generation falls back to
  // the local preview. `request` itself stays in `inflight` and keeps running —
  // a late success still writes the cache (subject to the generation guard).
  return withTimeout(request, PREVIEW_TIMEOUT_MS)
}

/**
 * Warm the cache for a creative without surfacing errors — intended for hover
 * prefetch where a failure should simply fall back to the on-open fetch.
 */
export function prefetchCreativePreview(
  businessID: string | number,
  creativeID: number,
  format?: FBAdPreviewFormat
): void {
  void fetchCreativePreview(businessID, creativeID, format).catch(() => {
    /* swallow: the modal's own fetch will surface the error if the user opens it */
  })
}

export function useFetchCreativePreview(
  creativeID?: number,
  defaultFormat?: FBAdPreviewFormat,
  // When the consumer renders its own "preview unavailable" UI (e.g. the sprint
  // packet modal's inline banner + local fallback), an error toast on top is
  // redundant and contradictory — let it opt out of the toast.
  { suppressErrorToast = false }: { suppressErrorToast?: boolean } = {}
) {
  const businessID = useGetBusinessID()
  const [loadingPreview, setIsLoadingPreview] = useState(false)
  // True while a forced refresh is in flight (even when stale HTML is still shown);
  // callers gate write actions like Approve on this so an AE can't act on the
  // pre-refresh preview before the live one resolves.
  const [refreshing, setRefreshing] = useState(false)
  const [freshPreviewReady, setFreshPreviewReady] = useState(false)
  const [adCreativePreview, setAdCreativePreview] = useState<string | undefined>(() =>
    creativeID ? peekCreativePreview(businessID, creativeID, defaultFormat) : undefined
  )
  const refreshSeqRef = useRef(0)

  // Mirror the displayed preview into a ref so refreshPreview can decide whether
  // to show a spinner (nothing on screen yet) or refresh silently behind the
  // currently-shown HTML, without re-creating the callback on every change.
  const previewRef = useRef(adCreativePreview)
  useEffect(() => {
    previewRef.current = adCreativePreview
  }, [adCreativePreview])

  const retrievePreview = useCallback(
    async (creativeIDOverride?: number) => {
      const id = creativeIDOverride ?? creativeID
      if (!id) return

      const cached = peekCreativePreview(businessID, id, defaultFormat)
      if (cached !== undefined) {
        setAdCreativePreview(cached)
        return
      }

      // Clear any preview from a previously-selected creative before fetching this
      // one, so a switch never leaves the prior creative's iframe on screen — both
      // while this fetch is in flight (spinner shows, gated by loadingPreview) and
      // if it fails (the consumer's local fallback + banner show instead).
      setAdCreativePreview(undefined)
      try {
        setIsLoadingPreview(true)
        setAdCreativePreview(await fetchCreativePreview(businessID, id, defaultFormat))
      } catch (e) {
        if (!suppressErrorToast)
          toast.error(
            `Failed to generate a preview from Facebook for your ad: ${getApiErrorMessage(
              e,
              'Please try again.'
            )}`
          )
        console.error(e)
      }
      setIsLoadingPreview(false)
    },
    [businessID, creativeID, defaultFormat, suppressErrorToast]
  )

  // Force a live refetch, bypassing (and clearing) any cached HTML. If a preview
  // is already on screen (e.g. hover-prefetched), keep showing it and swap to the
  // fresh result silently; otherwise show the spinner. Used on modal mount and on
  // tab-focus so an edit made in the Creative Wizard tab can never be approved
  // against stale HTML.
  const refreshPreview = useCallback(
    async (creativeIDOverride?: number) => {
      const id = creativeIDOverride ?? creativeID
      if (!id) return false

      const seq = ++refreshSeqRef.current
      invalidateCreativePreview(businessID, id, defaultFormat)
      const hadPreview = previewRef.current !== undefined
      setFreshPreviewReady(false)
      try {
        if (!hadPreview) setIsLoadingPreview(true)
        setRefreshing(true)
        const freshPreview = await fetchCreativePreview(businessID, id, defaultFormat)
        if (refreshSeqRef.current === seq) {
          setAdCreativePreview(freshPreview)
          setFreshPreviewReady(true)
        }
        return true
      } catch (e) {
        if (refreshSeqRef.current === seq) {
          setAdCreativePreview(undefined)
          setFreshPreviewReady(false)
          if (!suppressErrorToast)
            toast.error(
              `Failed to generate a preview from Facebook for your ad: ${getApiErrorMessage(
                e,
                'Please try again.'
              )}`
            )
        }
        console.error(e)
        return false
      } finally {
        if (refreshSeqRef.current === seq) {
          if (!hadPreview) setIsLoadingPreview(false)
          setRefreshing(false)
        }
      }
    },
    [businessID, creativeID, defaultFormat, suppressErrorToast]
  )

  return {
    loadingPreview,
    refreshing,
    freshPreviewReady,
    setIsLoadingPreview,
    retrievePreview,
    refreshPreview,
    adCreativePreview,
  }
}
